import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, ALPHA, ANIMATION, FONTS } from '../constants/theme';
import type { BoardPosition } from '@/types';

export type TileState = 'normal' | 'move' | 'dash' | 'attack' | 'selected' | 'preview-attack';
export type ArrowType = 'move' | 'dash' | null;

export interface TileSpriteOptions {
  position: BoardPosition;
  size: number;
  onClick?: (position: BoardPosition) => void;
}

// Arrow characters for different directions
const ARROWS: Record<string, string> = {
  'up': '↑',
  'down': '↓',
  'left': '←',
  'right': '→',
  'up-left': '↖',
  'up-right': '↗',
  'down-left': '↙',
  'down-right': '↘',
};

/**
 * Individual tile on the game board
 */
export class TileSprite extends Container {
  private background: Graphics;
  private highlight: Graphics;
  private arrowText: Text;
  private options: TileSpriteOptions;
  private currentState: TileState = 'normal';
  private currentArrow: string | null = null;
  private currentArrowType: ArrowType = null;
  private isHovered = false;

  constructor(options: TileSpriteOptions) {
    super();
    this.options = options;

    // Background
    this.background = new Graphics();
    this.addChild(this.background);

    // Highlight overlay
    this.highlight = new Graphics();
    this.highlight.visible = false;
    this.addChild(this.highlight);

    // Arrow text
    const arrowStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: options.size * 0.5,
      fill: 0x000000,
      fontWeight: 'bold',
    });
    this.arrowText = new Text({ text: '', style: arrowStyle });
    this.arrowText.anchor.set(0.5);
    this.arrowText.alpha = 0.6;
    this.arrowText.visible = false;
    this.addChild(this.arrowText);

    this.draw();
    this.setupInteraction();
  }

  private draw(): void {
    const { size } = this.options;
    const halfSize = size / 2;

    // Background
    this.background.clear();
    this.background.roundRect(-halfSize, -halfSize, size, size, 4);

    let bgColor: number = COLORS.TILE;
    if (this.isHovered && (this.currentState !== 'normal' || this.currentArrowType)) {
      bgColor = COLORS.TILE_HOVER;
    }
    this.background.fill({ color: bgColor });

    // Draw highlight based on state (only for attack/selected states, not move/dash)
    this.highlight.clear();
    const showHighlight = this.currentState === 'attack' ||
                          this.currentState === 'preview-attack' ||
                          this.currentState === 'selected';
    this.highlight.visible = showHighlight;

    if (showHighlight) {
      this.highlight.roundRect(-halfSize, -halfSize, size, size, 4);

      let color: number;
      switch (this.currentState) {
        case 'attack':
        case 'preview-attack':
          color = COLORS.TILE_ATTACK;
          break;
        case 'selected':
          color = COLORS.TILE_SELECTED;
          break;
        default:
          color = COLORS.TILE;
      }

      this.highlight.fill({ color, alpha: ALPHA.TILE_HIGHLIGHT });

      // Border for selected tile
      if (this.currentState === 'selected') {
        this.highlight.roundRect(-halfSize, -halfSize, size, size, 4);
        this.highlight.stroke({ color: COLORS.TILE_SELECTED, width: 3 });
      }

      // Dashed border for preview attack
      if (this.currentState === 'preview-attack') {
        this.highlight.roundRect(-halfSize + 2, -halfSize + 2, size - 4, size - 4, 4);
        this.highlight.stroke({ color: COLORS.TILE_ATTACK, width: 2, alpha: 0.8 });
      }
    }

    // Update arrow - show independently of tile state, colored by arrow type
    this.arrowText.visible = !!this.currentArrow && !!this.currentArrowType;
    if (this.currentArrow && this.currentArrowType) {
      this.arrowText.text = this.currentArrow;
      // Color based on arrow type: green for move, blue for dash
      const arrowColor = this.currentArrowType === 'dash' ? COLORS.TILE_VALID_DASH : COLORS.TILE_VALID_MOVE;
      this.arrowText.style.fill = arrowColor;
      this.arrowText.style.fontSize = this.currentArrowType === 'dash' ? size * 0.6 : size * 0.5;
      this.arrowText.alpha = 0.9;
    }
  }

  private setupInteraction(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerover', () => {
      this.isHovered = true;
      this.draw();
      if (this.currentState !== 'normal') {
        gsap.to(this.scale, { x: 1.05, y: 1.05, duration: ANIMATION.HOVER_DURATION });
      }
    });

    this.on('pointerout', () => {
      this.isHovered = false;
      this.draw();
      gsap.to(this.scale, { x: 1, y: 1, duration: ANIMATION.HOVER_DURATION });
    });

    this.on('pointerdown', () => {
      this.options.onClick?.(this.options.position);
    });
  }

  /**
   * Update the tile's visual state
   */
  setState(state: TileState): void {
    this.currentState = state;
    // Clear arrow when state changes to non-movement states
    if (state === 'normal' || state === 'selected') {
      this.currentArrow = null;
      this.currentArrowType = null;
    }
    this.draw();
  }

  /**
   * Set the arrow indicator (can be set independently of state)
   */
  setArrow(arrow: string | null, type: ArrowType): void {
    this.currentArrow = arrow;
    this.currentArrowType = type;
    this.draw();
  }

  /**
   * Get the tile's position
   */
  getPosition(): BoardPosition {
    return this.options.position;
  }

  /**
   * Update tile size
   */
  setSize(size: number): void {
    this.options.size = size;
    this.arrowText.style.fontSize = size * 0.5;
    this.draw();
  }

  /**
   * Get arrow character for a direction from selected position to this tile
   */
  static getArrowForDirection(fromRow: number, fromCol: number, toRow: number, toCol: number): string {
    const rowDiff = toRow - fromRow;
    const colDiff = toCol - fromCol;

    if (rowDiff < 0 && colDiff === 0) return ARROWS['up'];
    if (rowDiff > 0 && colDiff === 0) return ARROWS['down'];
    if (rowDiff === 0 && colDiff < 0) return ARROWS['left'];
    if (rowDiff === 0 && colDiff > 0) return ARROWS['right'];
    if (rowDiff < 0 && colDiff < 0) return ARROWS['up-left'];
    if (rowDiff < 0 && colDiff > 0) return ARROWS['up-right'];
    if (rowDiff > 0 && colDiff < 0) return ARROWS['down-left'];
    if (rowDiff > 0 && colDiff > 0) return ARROWS['down-right'];

    return '';
  }
}
